// packages/pulse-sdk/src/hooks.ts
// ─────────────────────────────────────────────────────────────────────────────
// Data hooks. Every hook is a thin TanStack Query wrapper over PulseClient.
// They never own auth — the host's getToken() (via PulseProvider) supplies the
// token per request. Query keys are namespaced under "pulse" and parameterized
// by participantId so cache entries don't collide across participants.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { usePulse, usePulseClient } from "./context";
import { qs } from "./client";
import type {
  Me,
  BodyMetric,
  BodyMetricsQuery,
  ArcResponse,
  ArcTile,
  ChatStatus,
  SpecialistInfo,
  SignalSummary,
  GuardianGateResult,
} from "./types";

// ── useAuth ───────────────────────────────────────────────────────────────────
// Surfaces the synchronous best-effort { user, scopes } the provider derives
// from the host token, plus an isLoading flag for the very first token read.
export interface UseAuthResult {
  user: string | null;
  scopes: string[];
  isLoading: boolean;
}

export function useAuth(): UseAuthResult {
  const { auth, refreshAuth } = usePulse();
  const [isLoading, setIsLoading] = useState(auth.user === null);

  useEffect(() => {
    let cancelled = false;
    void refreshAuth().finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // refreshAuth identity is stable enough across renders for an initial seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { user: auth.user, scopes: auth.scopes, isLoading };
}

// ── useMe ───────────────────────────────────────────────────────────────────
export function useMe(): UseQueryResult<Me> {
  const client = usePulseClient();
  return useQuery({
    queryKey: ["pulse", "me"],
    queryFn: () => client.get<Me>("/api/pulse/me"),
  });
}

// ── useSpecialist ─────────────────────────────────────────────────────────────
export function useSpecialist(
  participantId?: number
): UseQueryResult<SpecialistInfo> {
  const client = usePulseClient();
  return useQuery({
    queryKey: ["pulse", "specialist", participantId ?? "self"],
    queryFn: () =>
      client.get<SpecialistInfo>(
        `/api/pulse/specialist${qs({ participantId })}`
      ),
  });
}

// ── useSignal ─────────────────────────────────────────────────────────────────
// Sanitized signal dimensions only (5 labels + 0..1 scores). The raw
// signal_profile never leaves the server.
export function useSignal(
  participantId?: number
): UseQueryResult<SignalSummary> {
  const client = usePulseClient();
  return useQuery({
    queryKey: ["pulse", "signal", participantId ?? "self"],
    queryFn: () =>
      client.get<SignalSummary>(`/api/pulse/signal${qs({ participantId })}`),
  });
}

// ── useBodyMetrics ────────────────────────────────────────────────────────────
// Body metrics are self-only on the server (the authed user's rows). The
// `participantId` arg is accepted for API symmetry with the other hooks and to
// namespace the cache, but the server resolves the authed user regardless.
export function useBodyMetrics(
  participantId?: number,
  query: BodyMetricsQuery = {}
): UseQueryResult<BodyMetric[]> {
  const client = usePulseClient();
  const { from, to, source } = query;
  return useQuery({
    queryKey: ["pulse", "body-metrics", participantId ?? "self", from, to, source],
    queryFn: async () => {
      const res = await client.get<{ items: BodyMetric[] }>(
        `/api/pulse/body-metrics${qs({ from, to, source })}`
      );
      return res.items;
    },
  });
}

// ── useArc ────────────────────────────────────────────────────────────────────
// The server composes & ranks ARC tiles. The client only receives the public
// per-tile fields (relevanceScore + signalDimension label); the score
// components and signal_profile stay server-side.
export interface UseArcOptions {
  max?: number;
}

export function useArc(
  participantId?: number,
  options: UseArcOptions = {}
): UseQueryResult<ArcTile[]> {
  const client = usePulseClient();
  const { max } = options;
  return useQuery({
    queryKey: ["pulse", "arc", participantId ?? "self", max],
    queryFn: async () => {
      const res = await client.get<ArcResponse>(
        `/api/pulse/arc${qs({ participantId, max })}`
      );
      return res.tiles;
    },
  });
}

// ── useGuardianGate ─────────────────────────────────────────────────────────────
// Backs <GuardianGate/>. The server audits on every successful call, so we
// disable caching (staleTime 0) — each mount/refetch records access. A failed
// gate surfaces as a PulseApiError(403) on `error`.
export function useGuardianGate(
  participantId?: number
): UseQueryResult<GuardianGateResult> {
  const client = usePulseClient();
  return useQuery({
    queryKey: ["pulse", "guardian-gate", participantId ?? "self"],
    queryFn: () =>
      client.get<GuardianGateResult>(
        `/api/pulse/guardian-gate${qs({ participantId })}`
      ),
    staleTime: 0,
    gcTime: 0,
  });
}

// ── useChatStatus ─────────────────────────────────────────────────────────────
// Backs <PulseThread/> until the sanitized thread-read endpoint ships (Phase 5).
// Reads the engine's intake status surface (GET /api/pulse/status, pulse.chat).
export function useChatStatus(): UseQueryResult<ChatStatus> {
  const client = usePulseClient();
  return useQuery({
    queryKey: ["pulse", "chat-status", "self"],
    queryFn: () => client.get<ChatStatus>("/api/pulse/status"),
  });
}
